import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { FiClock, FiList, FiCheckCircle, FiCheck, FiCalendar } from "react-icons/fi";
import "./MyExamTopics.css";
import { useProtectedRoute } from "../../hooks/useProtectedRoute";
import { useColorMode } from "../../context/ColorModeContext";

interface ImagePreview {
  file: File;
  preview: string;
  id: string;
}

interface Message {
  role: "system" | "user" | "assistant";
  content: string;
  isUser: boolean;
  timestamp: Date;
  images?: ImagePreview[];
  audio?: {
    audioUrl: string;
    duration: number;
  };
}

interface ExamTopic {
  _id: string;
  sessionId: string;
  subject: string;
  examName: string;
  topics: any[];
  createdAt: string;
}

const calculateCardProgress = (topic: ExamTopic) => {
  if (!topic.topics || topic.topics.length === 0) {
    return { percentage: 0, totalSteps: 0, completedSteps: 0, totalEstimatedDays: 0, daysDue: 0 };
  }

  let totalSteps = 0;
  let completedSteps = 0;
  let totalEstimatedDays = 0;

  topic.topics.forEach(t => {
    totalEstimatedDays += (t.TotalEstimatedTimeDays || t.ToalEstimatedTimeDays || 0);
    if (t.subtopics) {
      t.subtopics.forEach((sub: any) => {
        totalSteps++;
        if (sub.completed) completedSteps++;
      });
    }
  });

  const percentage = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  // Calculate approximate days due if we use created at + total estimated days
  const createdDate = new Date(topic.createdAt);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - createdDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const daysDue = Math.max(0, totalEstimatedDays - diffDays);

  return { percentage, totalSteps, completedSteps, totalEstimatedDays, daysDue };
};

const MyExamTopics: React.FC = () => {
  useProtectedRoute(); // Protect route from direct URL access when credits exhausted

  const navigate = useNavigate();
  const { mode } = useColorMode();
  const [examTopics, setExamTopics] = useState<ExamTopic[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedTopic, setSelectedTopic] = useState<ExamTopic | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [incompleteTopic, setIncompleteTopic] = useState<string>("");
  const [incompleteSubtopic, setIncompleteSubtopic] = useState<string>("");
  const [isLoadingIncomplete, setIsLoadingIncomplete] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>("");
  const [isExamMode, setIsExamMode] = useState(false);
  const [isMyTopicsOpen, setIsMyTopicsOpen] = useState(true);
  const [isExamResultOpen, setIsExamResultOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [examResult, setExamResult] = useState<any>(null);
  const [examResultTitle, setExamResultTitle] = useState<string>("");
  const [fetchError, setFetchError] = useState<string>("");
  // Fetch exam topics when the component mounts
  useEffect(() => {
    const fetchExamTopics = async () => {
      setIsLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setError("Please login to view your exam topics");
          return;
        }

        const response = await api.get("/exam-topics/mine?limit=20&page=1");
        if (response.data && response.data.success) {
          console.log("📚 Fetched exam topics:", response.data.data);
          setExamTopics(Array.isArray(response.data.data) ? response.data.data : []);
        } else {
          setError("Failed to fetch exam topics");
        }
      } catch (err) {
        console.error("Error fetching exam topics:", err);
        setError("An error occurred while fetching exam topics");
      } finally {
        setIsLoading(false);
      }
    };

    fetchExamTopics();
  }, []);

  // Group exam topics by examName
  const groupedTopics = examTopics.reduce((acc, topic) => {
    const examName = topic.examName || "Uncategorized";
    if (!acc[examName]) {
      acc[examName] = [];
    }
    acc[examName].push(topic);
    return acc;
  }, {} as { [key: string]: ExamTopic[] });

  // Function to find incomplete topics
  const findIncompleteTopics = async (subject: string, examName: string) => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No token found. Please login first.");
      setFetchError("Please login to continue");
      return null;
    }

    try {
      setIsLoadingIncomplete(true);
      const url = `${process.env.REACT_APP_API_URL}/api/exam-topics/incomplete`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ subject, examName }),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `Request failed with status ${res.status}`);
      }

      const data = await res.json();

      if (data.message === "All topics are completed") {
        setIncompleteTopic("");
        setIncompleteSubtopic("");
        console.log("🎉 All topics are completed!");
        return { topic: null, subtopic: null, allCompleted: true };
      }

      if (data.topic) {
        setIncompleteTopic(data.topic);
        setIncompleteSubtopic(data.subtopic || "");
        console.log("📚 Incomplete Topic Found:", data.topic);
        console.log("📖 Incomplete Subtopic:", data.subtopic || "None");
        return { topic: data.topic, subtopic: data.subtopic, allCompleted: false };
      }

      return null;
    } catch (error: any) {
      console.error("Error finding incomplete topics:", error);
      setIncompleteTopic("");
      setIncompleteSubtopic("");
      setFetchError("Failed to fetch incomplete topics");
      return null;
    } finally {
      setIsLoadingIncomplete(false);
    }
  };

  const handleExamTopicClick = async (topic: ExamTopic) => {
    setSelectedTopic(topic);
    const documentId = topic.sessionId || topic._id; // Fallback to _id if sessionId is missing
    if (!documentId) {
      console.warn("⚠️ No sessionId or _id found for topic:", topic);
      setFetchError("Invalid topic selected. Please try another topic.");
      return;
    }

    // Fetch incomplete topics so we can focus on the right place
    const incompleteData = await findIncompleteTopics(topic.subject, topic.examName);

    navigate(`/study-plan/${documentId}`, {
      state: {
        topic,
        incompleteTopic: incompleteData?.topic || "",
        incompleteSubtopic: incompleteData?.subtopic || "",
        courseId: documentId,
      }
    });
  };

  const fetchExamModeChats = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      console.error("No token found. Please login first.");
      setFetchError("Please login to continue");
      return;
    }

    // Check if we have all required parameters
    if (
      !selectedDocumentId ||
      !selectedTopic?.subject ||
      !selectedTopic?.examName ||
      !incompleteTopic ||
      !incompleteSubtopic
    ) {
      console.log("⚠️ Missing required parameters for exam mode chats:", {
        courseId: selectedDocumentId,
        subject: selectedTopic?.subject,
        examName: selectedTopic?.examName,
        topic: incompleteTopic,
        subtopic: incompleteSubtopic,
      });
      setFetchError("Unable to load chats: Missing required information");
      return;
    }

    try {
      console.log("🔍 Fetching exam mode chats for:", {
        courseId: selectedDocumentId,
        subject: selectedTopic.subject,
        examName: selectedTopic.examName,
        topic: incompleteTopic,
        subtopic: incompleteSubtopic,
      });

      const url = `${process.env.REACT_APP_API_URL}/api/llm/exam-mode-chats?courseId=${encodeURIComponent(
        selectedDocumentId
      )}&subject=${encodeURIComponent(selectedTopic.subject)}&examName=${encodeURIComponent(
        selectedTopic.examName
      )}&topic=${encodeURIComponent(incompleteTopic)}&subtopic=${encodeURIComponent(incompleteSubtopic)}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || `Request failed with status ${res.status}`);
      }

      const data = await res.json();
      console.log("📊 Exam mode chats response:", data);

      if (data.success && data.data && Array.isArray(data.data)) {
        const convertedMessages: Message[] = data.data.map((msg: any, index: number) => {
          let images: ImagePreview[] | undefined = undefined;
          if (msg.imageUrls && Array.isArray(msg.imageUrls) && msg.imageUrls.length > 0) {
            images = msg.imageUrls.map((url: string, imgIndex: number) => ({
              file: new File([], `image-${index}-${imgIndex}`, { type: "image/png" }),
              preview: url,
              id: `exam-chat-img-${msg._id}-${imgIndex}`,
            }));
          }

          let audio: { audioUrl: string; duration: number } | undefined = undefined;
          if (msg.audioUrl && typeof msg.audioUrl === "string") {
            audio = {
              audioUrl: msg.audioUrl,
              duration: msg.audioDuration || 0,
            };
          }

          return {
            role: msg.role,
            content: msg.content || "",
            isUser: msg.role === "user",
            timestamp: new Date(msg.timestamp || msg.createdAt || Date.now()),
            images: images,
            audio: audio,
          };
        });

        setMessages(convertedMessages);
        console.log("✅ Exam mode messages loaded:", convertedMessages.length, "messages");
      } else {
        console.log("ℹ️ No previous exam mode messages found for this topic/subtopic");
        setMessages([]);
      }
    } catch (error: any) {
      console.error("❌ Error fetching exam mode chats:", error);
      setMessages([]);
      setFetchError("Failed to load exam mode chats");
    }
  };

  const renderTopicsTree = (showStudyTips = false) => {
    const topics = selectedTopic?.topics || [];
    if (!Array.isArray(topics) || topics.length === 0) {
      return (
        <p style={{ opacity: 0.8, color: "#888", textAlign: "center", margin: "20px 0", fontSize: 16 }}>
          No topics found.
        </p>
      );
    }

    return (
      <div className="topics-tree" style={{ display: "grid", rowGap: 20, width: "100%" }}>
        {topics.map((t: any, idx: number) => {
          const key = `${t.topic}-${idx}`;
          const tips = Array.isArray(t.studyTips) ? t.studyTips : [];
          const topicTotalDays = t.ToalEstimatedTimeDays || t.TotalEstimatedTimeDays || 0;
          const subtopics = Array.isArray(t.subtopics) ? t.subtopics : [];

          // Use backend completion status
          const topicCompleted = t.completed || false;
          const topicCompletionPercentage = t.completionPercentage ? parseInt(t.completionPercentage) : 0;

          return (
            <div
              key={key}
              className="topic-item"
              style={{
                background: "white",
                borderRadius: 12,
                marginBottom: 16,
                padding: 0,
                boxShadow: "0 2px 8px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                overflow: "hidden",
                position: "relative",
                opacity: 0,
                transform: "translateY(10px)",
                animation: `slideIn 0.4s ease-out ${idx * 0.1}s forwards`,
                border: incompleteTopic === t.topic ? "3px solid #3b82f6" : "1px solid transparent",
              }}
            >
              {/* Topic Header */}
              <div
                className="topic-header"
                style={{
                  padding: "20px 24px 16px",
                  background:
                    incompleteTopic === t.topic
                      ? "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)"
                      : "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  borderBottom: "2px solid #f1f5f9",
                  color: "#1e293b",
                  fontSize: 18,
                  fontWeight: 700,
                  position: "relative",
                }}
              >
                {incompleteTopic === t.topic && (
                  <div
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      background: "#3b82f6",
                      color: "white",
                      fontSize: 10,
                      padding: "2px 6px",
                      borderRadius: 8,
                      fontWeight: 600,
                    }}
                  >
                    NEXT
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {!showStudyTips && (
                    <label
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: topicCompleted
                          ? "linear-gradient(135deg, #10b981, #059669)"
                          : "white",
                        border: topicCompleted ? "none" : "2px solid #cbd5e1",
                        cursor: "default",
                        flexShrink: 0,
                        position: "relative",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={topicCompleted}
                        readOnly={true}
                        style={{
                          position: "absolute",
                          width: "100%",
                          height: "100%",
                          margin: 0,
                          opacity: 0,
                          cursor: "default",
                        }}
                      />
                      {topicCompleted && (
                        <FiCheck
                          style={{
                            color: "white",
                            fontSize: 16,
                            pointerEvents: "none",
                            userSelect: "none",
                            zIndex: 1,
                          }}
                        />
                      )}
                    </label>
                  )}
                  <span style={{ fontSize: 20, color: "#334155" }}>{t.topic || `Topic ${idx + 1}`}</span>
                </div>
                <span
                  style={{
                    marginLeft: "auto",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    color: "#64748b",
                    fontSize: 16,
                    fontWeight: 500,
                    background: "#f1f5f9",
                    padding: "6px 12px",
                    borderRadius: 20,
                  }}
                >
                  <FiClock style={{ fontSize: 16 }} />
                  {topicTotalDays ? `${topicTotalDays}d` : "—"}
                </span>
              </div>

              {/* Progress bar for topic completion */}
              {!showStudyTips && (
                <div style={{ padding: "0 24px", marginBottom: 12 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: 4,
                    }}
                  >
                    <span style={{ fontSize: 14, color: "#64748b" }}>Progress</span>
                    <span style={{ fontSize: 14, color: "#64748b", fontWeight: 500 }}>
                      {topicCompletionPercentage}%
                    </span>
                  </div>
                  <div
                    style={{
                      height: 8,
                      background: "#e2e8f0",
                      borderRadius: 4,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${topicCompletionPercentage}%`,
                        background:
                          topicCompletionPercentage > 70
                            ? "linear-gradient(90deg, #10b981, #059669)"
                            : topicCompletionPercentage > 30
                              ? "linear-gradient(90deg, #f59e0b, #d97706)"
                              : "linear-gradient(90deg, #ef4444, #dc2626)",
                        borderRadius: 4,
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Topic Body */}
              <div
                className="topic-body"
                style={{
                  padding: "0 24px 24px",
                  display: "grid",
                  rowGap: 24,
                  background: "linear-gradient(180deg, #ffffff 0%, #fefefe 100%)",
                }}
              >
                {/* Subtopics Section */}
                <div className="subtopics" style={{ display: "grid", rowGap: 16 }}>
                  <div
                    style={{
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 12,
                      color: "#475569",
                      fontSize: 17,
                      marginBottom: 8,
                    }}
                  >
                    <div
                      style={{
                        background: "linear-gradient(135deg, #3b82f6, #1d4ed8)",
                        borderRadius: "50%",
                        padding: 8,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <FiList style={{ fontSize: 16, color: "white" }} />
                    </div>
                    Subtopics
                  </div>
                  {subtopics.length > 0 ? (
                    <div style={{ display: "grid", rowGap: 10 }}>
                      {subtopics.map((subtopic: any, sIdx: number) => {
                        const subtopicCompleted = subtopic.completed || false;

                        return (
                          <div
                            key={`${key}-sub-${sIdx}`}
                            style={{
                              background: incompleteSubtopic === subtopic.name ? "#fef3c7" : "#ffffff",
                              borderRadius: 10,
                              padding: "16px 20px",
                              margin: 0,
                              color: "#334155",
                              lineHeight: 1.6,
                              fontSize: 15,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                              border:
                                incompleteSubtopic === subtopic.name
                                  ? "2px solid #f59e0b"
                                  : "1px solid #e2e8f0",
                              transition: "all 0.2s ease",
                              cursor: "default",
                              transform: "translateX(0)",
                              animation: `slideInLeft 0.3s ease-out ${(sIdx * 0.05) + 0.2}s forwards`,
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateX(4px)";
                              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateX(0)";
                              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                              {!showStudyTips && (
                                <label
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 20,
                                    height: 20,
                                    borderRadius: "50%",
                                    background: subtopicCompleted
                                      ? "linear-gradient(135deg, #10b981, #059669)"
                                      : "white",
                                    border: subtopicCompleted ? "none" : "2px solid #cbd5e1",
                                    cursor: "default",
                                    flexShrink: 0,
                                    position: "relative",
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={subtopicCompleted}
                                    readOnly={true}
                                    style={{
                                      position: "absolute",
                                      width: "100%",
                                      height: "100%",
                                      margin: 0,
                                      opacity: 0,
                                      cursor: "default",
                                    }}
                                  />
                                  {subtopicCompleted && (
                                    <FiCheck
                                      style={{
                                        color: "white",
                                        fontSize: 12,
                                        pointerEvents: "none",
                                        userSelect: "none",
                                        zIndex: 1,
                                      }}
                                    />
                                  )}
                                </label>
                              )}
                              <span style={{ fontWeight: 500 }}>{subtopic.name}</span>
                              {incompleteSubtopic === subtopic.name && (
                                <span
                                  style={{
                                    background: "#f59e0b",
                                    color: "white",
                                    fontSize: 10,
                                    padding: "2px 6px",
                                    borderRadius: 8,
                                    fontWeight: 600,
                                  }}
                                >
                                  NEXT
                                </span>
                              )}
                            </div>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                color: "#64748b",
                                fontSize: 13,
                                background: "#f8fafc",
                                padding: "4px 8px",
                                borderRadius: 12,
                                fontWeight: 500,
                              }}
                            >
                              <FiClock style={{ fontSize: 12 }} />
                              {subtopic.estimatedTimeDays}d
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ color: "#94a3b8", fontStyle: "italic", margin: 0, fontSize: 15 }}>
                      No subtopics available.
                    </p>
                  )}
                </div>

                {/* Study Tips Section */}
                {showStudyTips && tips.length > 0 && (
                  <div className="tips" style={{ display: "grid", rowGap: 16 }}>
                    <div
                      style={{
                        fontWeight: 700,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 12,
                        color: "#475569",
                        fontSize: 17,
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          background: "linear-gradient(135deg, #10b981, #059669)",
                          borderRadius: "50%",
                          padding: 8,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <FiCheckCircle style={{ fontSize: 16, color: "white" }} />
                      </div>
                      Study Tips
                    </div>
                    <div style={{ display: "grid", rowGap: 10 }}>
                      {tips.map((tip: string, tIdx: number) => (
                        <div
                          key={`${key}-tip-${tIdx}`}
                          style={{
                            background: "#ffffff",
                            borderRadius: 10,
                            padding: "14px 18px",
                            margin: 0,
                            color: "#334155",
                            fontSize: 14,
                            lineHeight: 1.5,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            border: "1px solid #e2e8f0",
                            transition: "all 0.2s ease",
                            cursor: "default",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10,
                            animation: `slideInRight 0.3s ease-out ${(tIdx * 0.05) + 0.3}s forwards`,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
                          }}
                        >
                          <div
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: "linear-gradient(135deg, #f59e0b, #d97706)",
                              flexShrink: 0,
                              marginTop: 6,
                            }}
                          />
                          <span style={{ fontWeight: 400 }}>{tip}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100px" }}>
          <button
            className="resume-course"
            disabled={!selectedDocumentId || !incompleteTopic || !incompleteSubtopic}
            onClick={async () => {
              console.log("🚀 Resume Course button clicked!");
              console.log("📋 Current examResultTitle:", examResultTitle);
              console.log("📊 Current examResult:", examResult);
              console.log("🎯 Current incompleteTopic:", incompleteTopic);
              console.log("📖 Current incompleteSubtopic:", incompleteSubtopic);
              console.log("📄 Current selectedDocumentId:", selectedDocumentId);
              console.log("🔍 BEFORE: isExamMode =", isExamMode);

              // Set exam mode to true
              setIsExamMode(true);
              setIsMyTopicsOpen(false);
              setIsExamResultOpen(false);
              setIsModalOpen(false); // Close the modal

              console.log("✅ State updated - isExamMode set to true");
              console.log("🔄 Modals closed");
              console.log("🔍 AFTER: isExamMode =", true);
              navigate("/chat", {
                state: {
                  isExamMode: true,
                  courseId: selectedDocumentId,
                  subject: selectedTopic?.subject,
                  examName: selectedTopic?.examName,
                  incompleteTopic: incompleteTopic,
                  incompleteSubtopic: incompleteSubtopic || undefined,
                },
              });

              // Fetch previous exam mode chat messages
              console.log("💬 Loading previous exam mode chats...");
              await fetchExamModeChats();
            }}
            style={{
              background: !selectedDocumentId || !incompleteTopic || !incompleteSubtopic ? "#d1d5db" : "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: 8,
              padding: "12px 24px",
              fontSize: 16,
              fontWeight: 500,
              cursor: !selectedDocumentId || !incompleteTopic || !incompleteSubtopic ? "not-allowed" : "pointer",
            }}
          >
            Resume Course
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`my-exam-topics-container ${mode}`}>
      <h1 className="page-title">Your Plans</h1>
      {isLoading && <p className="loading-state">Loading exam topics...</p>}
      {error && (
        <div className="sessions-error" style={{ color: "#ef4444", fontSize: 12, marginTop: 8 }}>
          {error}
        </div>
      )}
      {fetchError && (
        <div className="fetch-error" style={{ color: "#ef4444", fontSize: 12, marginTop: 8 }}>
          {fetchError}
        </div>
      )}
      {!isLoading && !error && examTopics.length === 0 && <p>No exam topics found.</p>}
      {!isLoading && examTopics.length > 0 && (
        <div className="exam-topics-groups">
          {Object.keys(groupedTopics).map((examName) => (
            <div key={examName} className="exam-group">
              <div className="group-header">
                <div className="group-info">
                  <h2 className="group-title">{examName}</h2>
                  <span className="exam-count-badge">{groupedTopics[examName].length}</span>
                </div>
              </div>
              <div
                className="group-cards"
              >
                {groupedTopics[examName].map((topic) => {
                  const progress = calculateCardProgress(topic);
                  return (
                    <div
                      key={topic.sessionId || topic._id}
                      className="plan-card"
                      onClick={() => handleExamTopicClick(topic)}
                    >
                      <h3 className="plan-card-title">{topic.subject}</h3>

                      <div className="plan-card-stats-row">
                        <span className="plan-card-percentage">{progress.percentage}% Complete</span>
                        <span className="plan-card-steps">{progress.completedSteps}/{progress.totalSteps} Steps</span>
                      </div>

                      <div className="plan-card-progress-track">
                        <div
                          className="plan-card-progress-fill"
                          style={{ width: `${progress.percentage}%` }}
                        ></div>
                      </div>

                      <div className="plan-card-footer">
                        <FiCalendar className="calendar-icon" />
                        <span className="due-badge">Due: {progress.daysDue} Days</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyExamTopics;